import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { ToastService } from '../../services/toast.service';

/**
 * Pulls the most useful error message out of an HttpErrorResponse.
 *
 * API Maker returns errors in this shape (even for HTTP 500 application errors
 * thrown from custom-API code):
 *
 *   { success: false, statusCode: 500, errors: [{ code, message }], warnings: [] }
 *
 * so we look in `error.error.errors[0].message` first, then fall back to common
 * shapes (`error.error.message`, `error.message`).
 */
function extractAMError(err: any): string | null {
  const body = err?.error;
  if (body) {
    if (Array.isArray(body.errors) && body.errors.length) {
      const first = body.errors[0];
      if (first?.message) return String(first.message);
    }
    if (typeof body.message === 'string') return body.message;
  }
  if (typeof err?.message === 'string' && err.message) return err.message;
  return null;
}

export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const toast = inject(ToastService);

  return next(req).pipe(
    catchError((error) => {
      // 401 is owned by authInterceptor (it triggers sessionExpired) — stay silent.
      if (error.status === 401) return throwError(() => error);

      // Network failures (status 0) get a connectivity message regardless.
      if (error.status === 0) {
        toast.error('Unable to connect to the server. Please check your internet connection.');
        return throwError(() => error);
      }

      // Prefer the actual API Maker error message — it's almost always more
      // helpful than the generic status-based fallback.
      const message =
        extractAMError(error) ||
        (error.status === 403 && 'You do not have permission to perform this action.') ||
        (error.status === 404 && 'The requested resource was not found.') ||
        'An unexpected error occurred. Please try again later.';

      toast.error(message);
      return throwError(() => error);
    }),
  );
};
